import { useRef, useEffect } from 'react';

/**
 * Observe an HTMLElement with ResizeObserver.
 *
 * Usage:
 *
 * const ref = useResizeObserverRef((entry: ResizeObserverEntry) => {
 *    const height = entry.target.clientHeight;
 *    setHeight(height);
 * });
 * return <div ref={ref} />
 */
export default function useResizeObserverRef<T extends Element>(
  onResize: (entry: ResizeObserverEntry) => void
): React.RefObject<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        onResize(entry);
      }
    });

    const element = ref.current;
    if (element) {
      observer.observe(element);
    }
    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  });

  return ref;
}
